import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Plus, Loader2, ChevronRight, ShieldCheck, Send, CheckCircle2, XCircle, Check, X } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SeedBadge } from "@/components/common/SeedBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { ONBOARDING_STATUS, formatDate } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const FIELDS = [
  ["name", "Name"], ["city", "Ort"], ["region", "Region"], ["address", "Adresse"],
  ["manager", "Leitung"], ["phone", "Telefon"], ["email", "E-Mail"], ["category", "Kategorie"],
];
const EMPTY = { name: "", city: "", region: "", address: "", manager: "", phone: "", email: "", category: "", notes: "" };

export default function Onboarding() {
  const { can } = useAuth();
  const writable = can("onboarding", "write");
  const [items, setItems] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    api.get("/onboarding").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/onboarding/${id}`); setSelected(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding", form);
      toast.success("Onboarding-Kandidat angelegt");
      setCreateOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="onboarding-page">
      <PageHeader title="Onboarding" subtitle="Aufnahme, Profilprüfung und Datenqualität neuer Brockis (Emergent-Entwurf, noch kein Directorist-Write).">
        {writable && <Button data-testid="onboarding-create-button" onClick={() => setCreateOpen(true)} className="border border-fundio-gold bg-fundio-dark2 font-semibold text-fundio-cream hover:border-fundio-gold-highlight"><Plus className="h-4 w-4" /> Neuer Brocki</Button>}
      </PageHeader>

      <Panel testid="onboarding-panel" noPad>
        {items === null ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
        ) : items.length === 0 ? (
          <div className="p-4"><EmptyState icon={UserPlus} title="Keine Kandidaten" description="Legen Sie einen neuen Brocki-Onboarding-Kandidaten an." testid="onboarding-empty" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-fundio-border bg-fundio-subtle text-left text-xs font-semibold uppercase tracking-wider text-fundio-muted">
                <th className="px-5 py-3">Brocki</th><th className="px-5 py-3">Region</th><th className="px-5 py-3">Vollständigkeit</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-fundio-border">
              {items.map((c) => (
                <tr key={c.id} data-testid={`onboarding-row-${c.id}`} onClick={() => openDetail(c.id)} className="cursor-pointer bg-white transition-colors hover:bg-fundio-subtle">
                  <td className="px-5 py-3.5 font-medium text-fundio-ink">{c.name}<SeedBadge show={c.is_seed} /></td>
                  <td className="px-5 py-3.5 text-fundio-muted">{c.region || "—"}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-fundio-border"><div className="h-full rounded-full bg-fundio-gold" style={{ width: `${c.quality.completeness}%` }} /></div>
                      <span className="font-mono text-xs text-fundio-muted">{c.quality.completeness}%</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5"><StatusBadge map={ONBOARDING_STATUS} value={c.status} /></td>
                  <td className="px-5 py-3.5 text-right"><span className="inline-flex items-center gap-1 text-sm font-semibold text-fundio-ink">Öffnen <ChevronRight className="h-4 w-4" /></span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="onboarding-create-dialog">
          <DialogHeader><DialogTitle className="font-display">Neuer Brocki – Onboarding</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            {FIELDS.map(([k, label]) => (
              <div key={k} className={`space-y-1.5 ${k === "name" || k === "address" ? "col-span-2" : ""}`}>
                <Label className="text-xs text-fundio-muted">{label}</Label>
                <Input data-testid={`onboarding-field-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="input-dark" />
              </div>
            ))}
            <div className="col-span-2 space-y-1.5"><Label className="text-xs text-fundio-muted">Notizen</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-dark" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} className="text-fundio-muted">Abbrechen</Button>
            <Button data-testid="onboarding-save-button" disabled={saving || !form.name} onClick={create} className="border border-fundio-gold bg-fundio-dark2 font-semibold text-fundio-cream hover:border-fundio-gold-highlight">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anlegen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OnboardingDetail candidate={selected} onClose={() => setSelected(null)} writable={writable} onChanged={(c) => { setSelected(c); load(); }} />
    </div>
  );
}

function OnboardingDetail({ candidate, onClose, writable, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  if (!candidate) return null;
  const c = candidate;
  const q = c.quality || { completeness: 0, quality_score: 0, checks: [] };
  const closed = c.status === "freigegeben" || c.status === "abgelehnt";

  const act = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/onboarding/${c.id}/${path}`, body || {});
      toast.success("Aktualisiert");
      onChanged(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open={!!candidate} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="onboarding-detail-sheet" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-display text-fundio-ink">{c.name}<StatusBadge map={ONBOARDING_STATUS} value={c.status} /></SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Verification: data quality + completeness */}
          <div className="card-surface rounded-xl p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-fundio-ink"><ShieldCheck className="h-4 w-4 text-fundio-gold-deep" /> Verifizierung & Datenqualität</div>
            <div className="grid grid-cols-2 gap-4">
              <Metric label="Vollständigkeit" value={q.completeness} />
              <Metric label="Datenqualität" value={q.quality_score} />
            </div>
            <ul className="mt-4 space-y-1.5" data-testid="onboarding-checklist">
              {q.checks.map((ch) => (
                <li key={ch.key} className="flex items-center gap-2 text-xs">
                  {ch.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-status-success" /> : <XCircle className="h-3.5 w-3.5 text-status-danger" />}
                  <span className={ch.ok ? "text-fundio-muted" : "text-status-danger"}>{ch.label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Profile fields */}
          <div className="card-surface rounded-xl p-4">
            <div className="mb-3 text-sm font-semibold text-fundio-ink">Profildaten</div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              {FIELDS.map(([k, label]) => (
                <div key={k}><dt className="text-xs text-fundio-faint">{label}</dt><dd className="text-fundio-ink">{c[k] || "—"}</dd></div>
              ))}
            </dl>
            {c.notes && <p className="mt-3 border-t border-fundio-border pt-3 text-xs text-fundio-muted">{c.notes}</p>}
          </div>

          {c.status === "freigegeben" && (
            <div className="rounded-lg border border-status-warning-border bg-status-warning-bg/60 px-4 py-3 text-xs text-status-warning">
              Freigegeben. Bridge-Write nach Directorist ist <span className="font-semibold">vorbereitet, aber deaktiviert</span> (Phase 2A). Übertragung erfolgt erst nach Live-Verbindung der FUNDIO Bridge.
            </div>
          )}
          {c.status === "abgelehnt" && c.reject_reason && (
            <div className="rounded-lg border border-status-danger-border bg-status-danger-bg px-4 py-3 text-xs text-status-danger">Abgelehnt: {c.reject_reason}</div>
          )}
          <p className="text-[11px] text-fundio-faint">Angelegt {formatDate(c.created_at)} · zuletzt geändert {formatDate(c.updated_at)}{c.decided_by ? ` · Entscheidung: ${c.decided_by}` : ""}</p>

          {/* Actions by status */}
          {writable && !closed && (
            <div className="space-y-3">
              {c.status === "entwurf" && (
                <Button data-testid="onboarding-submit-button" disabled={busy} onClick={() => act("submit")} className="w-full border border-fundio-gold bg-fundio-dark2 font-semibold text-fundio-cream hover:border-fundio-gold-highlight"><Send className="h-4 w-4" /> Zur Prüfung einreichen</Button>
              )}
              {c.status === "in_pruefung" && (
                <Button data-testid="onboarding-verify-button" disabled={busy || q.completeness < 100} onClick={() => act("verify")} className="w-full border border-fundio-gold bg-fundio-dark2 font-semibold text-fundio-cream hover:border-fundio-gold-highlight disabled:opacity-50">
                  <ShieldCheck className="h-4 w-4" /> {q.completeness < 100 ? "Verifizierung (100% nötig)" : "Verifizieren"}
                </Button>
              )}
              {c.status === "verifiziert" && (
                <Button data-testid="onboarding-approve-button" disabled={busy} onClick={() => act("approve")} className="w-full bg-status-success font-semibold text-white hover:bg-status-success/90"><Check className="h-4 w-4" /> Freigeben</Button>
              )}
              {(c.status === "in_pruefung" || c.status === "verifiziert") && (
                <div className="flex gap-2">
                  <Input data-testid="onboarding-reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Ablehnungsgrund" className="input-dark" />
                  <Button variant="ghost" data-testid="onboarding-reject-button" disabled={busy} onClick={() => act("reject", { comment: rejectReason })} className="border border-status-danger-border text-status-danger hover:bg-status-danger-bg"><X className="h-4 w-4" /> Ablehnen</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="flex justify-between text-xs text-fundio-muted"><span>{label}</span><span className="font-mono">{value}%</span></div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-fundio-border"><div className={`h-full rounded-full ${value >= 100 ? "bg-status-success" : "bg-fundio-gold"}`} style={{ width: `${value}%` }} /></div>
    </div>
  );
}
